import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Spin, 
  Typography, 
  Select, 
  Space,
  Row,
  Col,
  App,
  Modal
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  TeamOutlined,
  ArrowLeftOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import userService from '../services/userService';
import businessUnitService from '../services/businessUnitService';
import PageHeader from './PageHeader';
import { BREADCRUMB_PATTERNS } from './BreadcrumbNav';
import PhoneNumberInput from './PhoneNumberInput';
import { type } from '@testing-library/user-event/dist/type';

const { Title, Text } = Typography;
const { Option } = Select;

const EditParticipant = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [participant, setParticipant] = useState(null);
  const [businessUnit, setBusinessUnit] = useState(null);
  const [selectedCountryCode, setSelectedCountryCode] = useState('+27');
  const [hasFormErrors, setHasFormErrors] = useState(false);
  
  // Department modal states
  const [addDepartmentModalVisible, setAddDepartmentModalVisible] = useState(false);
  const [newDepartmentName, setNewDepartmentName] = useState('');
  const [addingDepartment, setAddingDepartment] = useState(false);
  
  const { id, businessUnitId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const mode = searchParams.get('mode');
  
  // Determine if we're in edit mode based on URL pattern and presence of id
  const isEdit = (location.pathname.includes('/edit') && id) || (mode === 'edit' && id);
  // Determine if we're in create mode  
  const isCreate = (!isEdit && (location.pathname.includes('/new') || mode === 'create'));

  console.log('EditParticipant render - id:', id, 'businessUnitId:', businessUnitId, 'mode:', mode, 'isEdit:', isEdit, 'isCreate:', isCreate, 'pathname:', location.pathname);


  // Check for form errors whenever form changes
  useEffect(() => {
    const checkFormErrors = () => {
      const fieldErrors = form.getFieldsError();
      const hasErrors = fieldErrors.some(({ errors }) => errors.length > 0);
      setHasFormErrors(hasErrors);
    };

    // Check immediately
    checkFormErrors();

    // Set up form field listener
    const handleFieldsChange = () => {
      setTimeout(checkFormErrors, 100); // Small delay to ensure form state is updated
    };

    // Listen to form changes
    form.getInternalHooks('RC_FORM_INTERNAL_HOOKS').registerWatch(handleFieldsChange);

    return () => {
      // Cleanup if needed
    };
  }, [form]);

  
  useEffect(() => {
    console.log('EditParticipant useEffect - id:', id, 'mode:', mode, 'isEdit:', isEdit);
    if (isEdit && id) {

      loadParticipant();
    } else {
      // For new participant, just load business unit if we have businessUnitId
      if (businessUnitId) {
        console.log('Loading business unit for new participant:', businessUnitId);
        loadBusinessUnitForNew();
      } else {
        console.log('No businessUnitId, setting pageLoading to false');
        setPageLoading(false);
      }
    }
  }, [id, mode, isEdit, businessUnitId]);

  const loadBusinessUnitForNew = async () => {
    try {
      setPageLoading(true);
      const result = await businessUnitService.getBusinessUnitById(businessUnitId);
      if (result.success && result.data) {
        setBusinessUnit(result.data.data);
      }
    } catch (error) {
      console.error('Error loading business unit:', error);
      message.error('Failed to load business unit');
    } finally {
      setPageLoading(false);
    }
  };

  const loadParticipant = async () => {
    // This function will only be called when we're in edit mode
    if (!id) {
      console.log('Prevented loadParticipant - no id');
      setPageLoading(false);
      return;
    }

    try {
      setPageLoading(true);
      console.log('EditParticipant - loadParticipant called with ID from useParams:', id);
      
      const result = await userService.getParticipantById(id);

     
      if (result.success && result.data?.userType === 'participant' || result.data?.userType === 'manager') {
      
        const participantData = result.data;
        setParticipant(participantData);
       
        // Load business unit details
        if (participantData.businessUnit) {
          const buResult = await businessUnitService.getBusinessUnitById(participantData.businessUnit._id || participantData.businessUnit);
         // alert(buResult?.data.success ? 'Business unit loaded successfully' : 'Failed to load business unit');
          if (buResult?.data.success) {
            setBusinessUnit(buResult.data?.data);
          }
        }
        
        // Set form values
        form.setFieldsValue({
          name: participantData?.name,
          surname: participantData?.surname,
          email: participantData?.email,
          phoneNumber: participantData?.phoneNumber?.number || '',
          department: participantData?.department
        });
        
        //alert(`${participantData?.name} ${participantData?.department} loaded for editing.`);
        // Set country code if available
        if (participantData.phoneNumber?.countryCode) {
          setSelectedCountryCode(participantData?.phoneNumber?.countryCode);
        }
        
      } else {
        
        message.error('Participant not found');
        navigate('/dashboard/business-units');
      }
    } catch (error) {
      
      message.error('Failed to load participant details');
      navigate('/dashboard/business-units');
    } finally {
      setPageLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (hasFormErrors) {
      message.error('Please fix all form errors before proceeding');
      return;
    }

    // Validate at least one contact method is provided
    const hasEmail = values.email && values.email.trim() !== '';
    const hasPhone = values.phoneNumber && values.phoneNumber.trim() !== '';
    if (!hasEmail && !hasPhone) {
      message.error('At least one contact method (email or phone number) is required');
      return;
    }

    try {
      setLoading(true);
      
      // Prepare phone number object if provided
      const phoneNumberData = values.phoneNumber ? {
        countryCode: selectedCountryCode,
        number: values.phoneNumber,
        full: `${selectedCountryCode}${values.phoneNumber}`
      } : null;
      
      const result = isEdit 
        ? await userService.updateParticipant(id, {
            name: values.name,
            surname: values.surname,
            email: values.email || undefined,
            phoneNumber: phoneNumberData,
            department: values.department,
            businessUnitId: participant.businessUnit?._id || participant.businessUnit
          })
        : await businessUnitService.createParticipant({
            name: values.name,
            surname: values.surname,
            email: values.email || undefined,
            phoneNumber: phoneNumberData,
            department: values.department,
            businessUnitId: businessUnitId || businessUnit?._id
          });
      
      console.log(`EditParticipant - ${isEdit ? 'update' : 'create'}Participant called with ID:`, id);
      console.log(`EditParticipant - ${isEdit ? 'update' : 'create'}Participant result:`, result);

      if (result.success) {
        message.success(`Participant ${isEdit ? 'updated' : 'created'} successfully!`);
        // Go back to the business unit participant management page
        const targetBusinessUnitId = isEdit 
          ? (participant.businessUnit?._id || participant.businessUnit)
          : (businessUnitId || businessUnit?._id);
        if (targetBusinessUnitId) {
          navigate(`/dashboard/businessunit/${targetBusinessUnitId}`);
        } else {
          navigate('/dashboard/business-units');
        }
      } else {
        message.error(result.message || `${isEdit ? 'Update' : 'Creation'} failed`);
      }
    } catch (error) {
      message.error('Update failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle adding new department
  const handleAddDepartment = async () => {
    if (!newDepartmentName.trim()) {
      message.error('Please enter a department name');
      return;
    }

    const currentBusinessUnitId = businessUnitId || businessUnit?._id;
    if (!currentBusinessUnitId) {
      message.error('Business unit not found');
      return;
    }

    try {
      setAddingDepartment(true);
      const result = await businessUnitService.addDepartment(currentBusinessUnitId, newDepartmentName.trim());
      
      if (result.success) {
        message.success('Department added successfully!');
        
        // Refresh business unit data to get updated departments
        const businessUnitResult = await businessUnitService.getBusinessUnitById(currentBusinessUnitId);
        if (businessUnitResult.success) {
          setBusinessUnit(businessUnitResult.data.data);
        }
        
        // Set the new department as selected
        form.setFieldsValue({ department: newDepartmentName.trim() });
        
        // Close modal and reset
        setAddDepartmentModalVisible(false);
        setNewDepartmentName('');
      } else {
        message.error(result.message || 'Failed to add department');
      }
    } catch (error) {
      console.error('Add department error:', error);
      message.error('Failed to add department. Please try again.');
    } finally {
      setAddingDepartment(false);
    }
  };

  if (pageLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <PageHeader 
          title={isEdit ? "Edit Participant" : "Add New Participant"}
          subtitle={isEdit ? "Update participant information" : "Add a new participant to the team"}
          breadcrumbs={[
            'Dashboard', 
            'Business Units', 
            businessUnit?.name || (isEdit ? 'Team Management' : 'Business Unit'), 
            isEdit ? 'Edit Participant' : 'Add Participant'
          ]}
          breadcrumbClickHandlers={[
            () => navigate('/dashboard'), // Dashboard
            () => navigate('/dashboard/business-units'), // Business Units
            () => {
              // Navigate to the business unit team management
              const targetBusinessUnitId = isEdit 
                ? (participant?.businessUnit?._id || participant?.businessUnit || businessUnit?._id)
                : (businessUnitId || businessUnit?._id);
              if (targetBusinessUnitId) {
                navigate(`/dashboard/businessunit/${targetBusinessUnitId}`);
              } else {
                navigate('/dashboard/business-units');
              }
            }, // Business Unit Name
            null // Current page
          ]}
          showBackButton={true}
          onBack={() => {
            const targetBusinessUnitId = isEdit 
              ? (participant?.businessUnit?._id || participant?.businessUnit)
              : (businessUnitId || businessUnit?._id);
            if (targetBusinessUnitId) {
              navigate(`/dashboard/businessunit/${targetBusinessUnitId}`);
            } else {
              navigate('/dashboard/business-units');
            }
          }}
        />
      </div>
      
      <Row justify="center">
        <Col xs={24} sm={20} md={16} lg={12} xl={10}>
          <Card style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <div style={{ marginBottom: '24px' }}>
             
               <Title level={2} style={{ margin: 0, color: '#2E86AB' }}>
                 {participant?.name} {participant?.surname}
                </Title>
              <Text type="secondary">
                Business Unit: <strong>{businessUnit?.name || participant?.businessUnit?.name}</strong>
              </Text>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              disabled={loading}
            >
              <Form.Item
                name="name"
                label="First Name"
                rules={[
                  { required: true, message: 'Please enter first name' },
                  { min: 2, message: 'Name must be at least 2 characters' }
                ]}
              >
                <Input 
                  prefix={<UserOutlined />} 
                  placeholder="Enter first name"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="surname"
                label="Last Name"
                rules={[
                  { required: true, message: 'Please enter last name' },
                  { min: 2, message: 'Surname must be at least 2 characters' }
                ]}
              >
                <Input 
                  prefix={<UserOutlined />} 
                  placeholder="Enter last name"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email Address (Optional if phone provided)"
                rules={[
                  { type: 'email', message: 'Please enter a valid email address' }
                ]}
              >
                <Input 
                  prefix={<MailOutlined />} 
                  placeholder="Enter email address"
                  size="large"
                />
              </Form.Item>

              <PhoneNumberInput
                form={form}
                businessUnitId={businessUnit?.businessUnitId}
                user={participant}
                initialPhone={participant?.phoneNumber}
                initialCountryCode={selectedCountryCode}
                onCountryCodeChange={setSelectedCountryCode}
                onPhoneValidation={(isValid, phone, countryCode) => {
                  if (isValid) {
                    form.setFieldsValue({ phoneNumber: phone });
                  }
                }}
              />

              <Form.Item
                name="department"
                label="Department"
                rules={[
                  { required: true, message: 'Please select department' }
                ]}
              >
                <Input.Group compact>
                  <Select
                    value={form.getFieldValue('department')} // Show current value
                    onChange={(value) => form.setFieldsValue({ department: value })} // Handle changes
                    prefix={<TeamOutlined />}
                    placeholder="Select department"
                    size="large"
                    style={{ width: 'calc(100% - 40px)' }}
                  >
                    {businessUnit?.departments?.map(dept => (
                      <Option key={dept} value={dept}>
                        {dept}
                      </Option>
                    ))}
                  </Select>
                  <Button
                  
                    size="large"
                    icon={<PlusOutlined />}
                    style={{ width: '40px' }}
                    onClick={() => setAddDepartmentModalVisible(true)}
                    title="Add new department"
                  />
                </Input.Group>
              </Form.Item>

              <Form.Item style={{ marginTop: '30px' }}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Button 
                    onClick={() => {
                      const businessUnitId = participant?.businessUnit?._id || participant?.businessUnit;
                      if (businessUnitId) {
                        navigate(`/dashboard/businessunit/${businessUnitId}/members`);
                      } else {
                        navigate('/dashboard/business-units');
                      }
                    }}
                    size="large"
                  >
                    Cancel
                  </Button>
                  <Button
                    htmlType="submit"
                    size="large"
                    loading={loading}
                    disabled={hasFormErrors}
                    style={{ 
                      background: '#2E86AB',
                      borderColor: '#2E86AB'
                    }}
                  >
                    {isEdit ? 'Update' : 'Create'} Participant
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>

      {/* Add Department Modal */}
      <Modal
        title="Add New Department"
        open={addDepartmentModalVisible}
        onOk={handleAddDepartment}
        onCancel={() => {
          setAddDepartmentModalVisible(false);
          setNewDepartmentName('');
        }}
        confirmLoading={addingDepartment}
        okText="Add Department"
        okStyle={{ type:"default" }}
      >
        <Input
          placeholder="Enter department name"
          value={newDepartmentName}
          onChange={(e) => setNewDepartmentName(e.target.value)}
          onPressEnter={handleAddDepartment}
          autoFocus
        />
      </Modal>
    </div>
  );
};

export default EditParticipant;